<app-modal
  [isVisible]="isVisible()"
  [config]="modalConfig()"
  [warnBeforeClose]="hasUnsavedWork()"
  (confirmed)="onContinue()"
  (cancelled)="onDismiss()"
  (closed)="onDismiss()"
  (secondary)="onBack()"
>
  @if (isLoading()) {
    <app-modal-status
      variant="loading"
      [title]="'modals.create_framework.creating_status' | transloco"
      [description]="'modals.create_framework.creating_description' | transloco"
    >
      <app-huxley [state]="'thinking'" [size]="72" />
    </app-modal-status>
  } @else if (successMessage()) {
    <app-modal-status variant="success" [title]="successMessage()!">
      <app-huxley [state]="'success'" [size]="72" />
    </app-modal-status>
  } @else {
    <!-- Step Indicator. Timeline is click-navigable to any previously-
         visited step; `maxReachedStep` freezes the completed markers
         after backward navigation so the ✓ reflects what the user has
         *done*, not just where they currently are. Form state, staged
         imports, and predefined-template cache persist across
         click-navigation — jumping back is lossless. -->
    <app-step-indicator
      [steps]="activeStepConfigs()"
      [currentStep]="currentStep()"
      [maxReachedStep]="maxReachedStep()"
      [clickable]="true"
      (stepClicked)="onStepClicked($event)"
    ></app-step-indicator>

    <!-- ── STEP 1: LANGUAGE ──
         Locked in FIRST because the pick step's framework templates
         and the Review step's predefined-controls preview both read
         from `templateLanguage`. Choosing here means the whole flow
         renders in one language — no jarring "your preview refetched
         because you picked DA at the end" moment. -->
    @if (currentStep() === 'language') {
      <div class="step-body">
        <p class="step-hint">{{ 'language_step.hint_framework' | transloco }}</p>
        <app-language-picker
          [language]="templateLanguage()"
          (languageChange)="onTemplateLanguageChange($event!)"
          appearance="cards"
        />
      </div>
    }

    <!-- ── STEP 2: PICK FRAMEWORK ── -->
    @if (currentStep() === 'pick') {
      <div class="step-body">
        <div class="step-header-row">
          <p class="step-hint">{{ 'modals.create_framework.pick_intro' | transloco }}</p>
        </div>

        @if (error()) {
          <div
            role="alert"
            [class.denied-banner]="errorIsDenial()"
            [class.alert]="!errorIsDenial()"
          >
            {{ error() }}
          </div>
        }

        @if (
          templatePickerHandler.templatesLoading() || templatePickerHandler.frameworksLoading()
        ) {
          <app-picker-skeleton variant="spacious" [count]="4" />
        } @else {
          <app-framework-template-picker
            [handler]="templatePickerHandler"
            [blockInUse]="true"
            inUseTooltipKey="modals.create_framework.pick_in_use_title"
            (templateSelected)="selectFrameworkTemplate($event)"
          />
        }
      </div>
    }

    <!-- ── STEP 2: DETAILS ── -->
    @if (currentStep() === 'details') {
      <div class="step-body" [formGroup]="form">
        <div class="step-header-row">
          <p class="step-hint">{{ 'modals.create_framework.details_intro' | transloco }}</p>
          <span class="meta">{{
            'modals.create_framework.step_header_created_by'
              | transloco: { name: currentUserName() }
          }}</span>
        </div>

        @if (error()) {
          <div
            role="alert"
            [class.denied-banner]="errorIsDenial()"
            [class.alert]="!errorIsDenial()"
          >
            {{ error() }}
          </div>
        }

        @if (selectedTemplate(); as template) {
          <!-- Read-only echo of the Step 1 selection. Edit goes back to the
               picker rather than embedding a second selector here, so the
               form has exactly one source of truth for the framework. -->
          <div class="selected-framework">
            <app-framework-icon [type]="template.framework_type" size="small" />
            <span class="selected-framework-name">{{ template.name }}</span>
            <button class="link-btn" type="button" (click)="onBack()">
              <mat-icon>edit</mat-icon>
              <span>{{ 'modals.create_framework.pick_change' | transloco }}</span>
            </button>
          </div>
        }

        <div class="field">
          <label for="fw-name"
            >{{ 'modals.create_framework.field_name' | transloco }}
            <span class="req">*</span></label
          >
          <input
            id="fw-name"
            type="text"
            class="input"
            formControlName="name"
            [placeholder]="'modals.create_framework.name_placeholder' | transloco"
          />
        </div>

        <div class="field">
          <label for="fw-desc">{{ 'modals.create_framework.field_description' | transloco }}</label>
          <textarea
            id="fw-desc"
            class="input textarea"
            formControlName="description"
            [placeholder]="'modals.create_framework.description_placeholder' | transloco"
            rows="3"
          ></textarea>
        </div>

        <div class="row">
          <div class="field">
            <label for="fw-target-date">{{
              'modals.create_framework.field_target_date' | transloco
            }}</label>
            <input id="fw-target-date" type="date" class="input" formControlName="target_date" />
          </div>
          <div class="field">
            <span class="label-with-info">
              <label for="fw-audit-date">{{
                'modals.create_framework.field_audit_date' | transloco
              }}</label>
              <app-info-tip
                [text]="'modals.create_framework.audit_date_info' | transloco"
                [ariaLabel]="'modals.create_framework.audit_date_info_aria' | transloco"
              />
            </span>
            <input id="fw-audit-date" type="date" class="input" formControlName="audit_date" />
          </div>
        </div>

        <div class="field field-collapsible" [class.field-hidden]="!isTypeII()">
          <span class="label-with-info">
            <label for="fw-audit-period-start">{{
              'modals.create_framework.field_audit_period_start' | transloco
            }}</label>
            <app-info-tip
              [text]="'modals.create_framework.audit_period_start_info' | transloco"
              [ariaLabel]="'modals.create_framework.audit_period_start_info_aria' | transloco"
            />
          </span>
          <input
            id="fw-audit-period-start"
            type="date"
            class="input"
            formControlName="audit_period_start"
          />
        </div>
      </div>
    }

    <!-- ── STEP 3: IMPORT CONTROLS (managers only, optional) ── -->
    @if (currentStep() === 'import') {
      <div class="step-body">
        <p class="step-hint">
          {{ 'modals.create_framework.import_intro_main' | transloco }}
        </p>
        @if (!importHandler.selectedSourceFrameworkId()) {
          <p
            class="step-hint"
            [innerHTML]="'modals.create_framework.import_intro_secondary' | transloco"
          ></p>
        }

        @if (importPicker.linkError()) {
          <div class="alert">{{ importPicker.linkError() }}</div>
        }

        @if (!importHandler.selectedSourceFrameworkId()) {
          <!-- Stage 1: pick a source framework -->
          <div class="picker-search">
            <mat-icon class="picker-search-icon">search</mat-icon>
            <input
              class="picker-search-input"
              type="text"
              [placeholder]="'modals.create_framework.search_frameworks_placeholder' | transloco"
              [value]="importPicker.search()"
              (input)="importPicker.onSearchInput($event)"
            />
          </div>
          <div class="picker-list">
            @if (importHandler.loadingImportableFrameworks()) {
              <app-picker-skeleton variant="spacious" [count]="4" />
            } @else if (importHandler.filteredImportableFrameworks().length === 0) {
              <p class="picker-empty">
                {{
                  importHandler.importableFrameworks().length === 0
                    ? ('modals.create_framework.import_no_frameworks' | transloco)
                    : ('modals.create_framework.import_no_search_match' | transloco)
                }}
              </p>
            } @else {
              @for (fw of importHandler.filteredImportableFrameworks(); track fw.framework_id) {
                <button class="picker-item" (click)="importHandler.selectSourceFramework(fw)">
                  <app-framework-icon [type]="fw.framework_type" size="small" />
                  <span class="picker-item-name">
                    {{ fw.name }}
                    <small class="meta">
                      · {{ fw.control_count }}
                      {{ 'modals.create_framework.import_control_count_suffix' | transloco }}
                      @if (importHandler.ownerOrgLabel(fw); as owner) {
                        ·
                        {{ 'modals.create_framework.import_from_owner_prefix' | transloco }}
                        {{ owner }}
                      }
                    </small>
                  </span>
                  @if (importHandler.isSameOrgFramework(fw)) {
                    <span
                      class="import-tag import-tag-reuse"
                      [title]="'modals.create_framework.import_tag_reuse_title' | transloco"
                      >{{ 'modals.create_framework.import_tag_reuse' | transloco }}</span
                    >
                  } @else {
                    <span
                      class="import-tag import-tag-copy"
                      [title]="'modals.create_framework.import_tag_copy_title' | transloco"
                      >{{ 'modals.create_framework.import_tag_copy' | transloco }}</span
                    >
                  }
                  <mat-icon class="picker-item-arrow">chevron_right</mat-icon>
                </button>
              }
            }
          </div>
        } @else {
          <!-- Stage 2: pick controls from the chosen source -->
          <div class="step-header-row import-stage-header">
            <button class="link-btn" (click)="importHandler.backToSourceSelection()">
              <mat-icon>arrow_back</mat-icon>
              <span>{{ 'modals.create_framework.back_to_frameworks' | transloco }}</span>
            </button>
            @if (importHandler.selectedSourceFramework(); as src) {
              <span class="meta import-stage-title" [title]="src.name">{{ src.name }}</span>
            }
          </div>

          <div class="picker-search">
            <mat-icon class="picker-search-icon">search</mat-icon>
            <input
              class="picker-search-input"
              type="text"
              [placeholder]="'modals.create_framework.search_controls_placeholder' | transloco"
              [value]="importPicker.search()"
              (input)="importPicker.onSearchInput($event)"
            />
          </div>

          @if (
            !importHandler.loadingSourceFrameworkControls() &&
            importHandler.filteredSourceFrameworkControls().length > 0
          ) {
            <div class="step-header-row import-select-all-row">
              <mat-checkbox
                [checked]="importPickerSelectionState() === 'all'"
                [indeterminate]="importPickerSelectionState() === 'some'"
                (change)="toggleImportPickerAll()"
              >
                {{ 'modals.create_framework.import_select_all' | transloco }}
              </mat-checkbox>
              <span class="meta">
                {{
                  'modals.create_framework.import_selected_count'
                    | transloco
                      : {
                          selected: importPicker.selectedIds().size,
                          total: importHandler.filteredSourceFrameworkControls().length,
                        }
                }}
              </span>
            </div>
            @if (importPicker.selectedIds().size > maxControlsPerRequest) {
              <p class="alert import-warning">
                {{
                  'modals.create_framework.import_max_controls_warning'
                    | transloco: { max: maxControlsPerRequest }
                }}
              </p>
            }
          }
          <div class="picker-list">
            @if (importHandler.loadingSourceFrameworkControls()) {
              <app-picker-skeleton variant="spacious" [count]="5" />
            } @else if (importHandler.filteredSourceFrameworkControls().length === 0) {
              <p class="picker-empty">
                {{ 'modals.create_framework.no_controls_match' | transloco }}
              </p>
            } @else {
              @for (
                control of importHandler.filteredSourceFrameworkControls();
                track control.control_id
              ) {
                <button
                  class="picker-item"
                  [class.picker-item-selected]="importPicker.isSelected(control.control_id)"
                  (click)="importPicker.toggleSelection(control.control_id)"
                >
                  <mat-icon class="picker-item-arrow">
                    {{
                      importPicker.isSelected(control.control_id)
                        ? 'check_box'
                        : 'check_box_outline_blank'
                    }}
                  </mat-icon>
                  <span class="picker-item-name">
                    {{ control.name }}
                    @if (formatControlSubtitle(control); as sub) {
                      <small class="meta">· {{ sub }}</small>
                    }
                  </span>
                </button>
              }
            }
          </div>
        }
      </div>
    }

    <!-- ── STEP 4: REVIEW ── -->
    @if (currentStep() === 'review') {
      <div class="step-body">
        @if (error()) {
          <div
            role="alert"
            [class.denied-banner]="errorIsDenial()"
            [class.alert]="!errorIsDenial()"
          >
            {{ error() }}
          </div>
        }

        <p class="step-hint">{{ 'modals.create_framework.review_intro' | transloco }}</p>

        <div class="review-section">
          <p class="section-label">
            {{ 'modals.create_framework.review_details_heading' | transloco }}
          </p>
          <div class="review-grid">
            <div class="review-item">
              <span class="review-label">{{
                'modals.create_framework.review_type' | transloco
              }}</span>
              <span class="review-value">{{ selectedTypeLabel() }}</span>
            </div>
            <div class="review-item">
              <span class="review-label">{{
                'modals.create_framework.review_name' | transloco
              }}</span>
              <span class="review-value">{{ form.controls.name.value }}</span>
            </div>
            @if (form.controls.description.value) {
              <div class="review-item review-item-full">
                <span class="review-label">{{
                  'modals.create_framework.review_description' | transloco
                }}</span>
                <span class="review-value review-value-desc">{{
                  form.controls.description.value
                }}</span>
              </div>
            }
            @if (selectedOrg(); as org) {
              <div class="review-item">
                <span class="review-label">{{
                  'modals.create_framework.review_organization' | transloco
                }}</span>
                <span class="review-value">{{ org.name }}</span>
              </div>
            }
            @if (form.controls.target_date.value) {
              <div class="review-item">
                <span class="review-label">{{
                  'modals.create_framework.review_target_date' | transloco
                }}</span>
                <span class="review-value">{{ form.controls.target_date.value }}</span>
              </div>
            }
            @if (form.controls.audit_date.value) {
              <div class="review-item">
                <span class="review-label">{{
                  'modals.create_framework.review_audit_date' | transloco
                }}</span>
                <span class="review-value">{{ form.controls.audit_date.value }}</span>
              </div>
            }
            @if (form.controls.audit_period_start.value) {
              <div class="review-item">
                <span class="review-label">{{
                  'modals.create_framework.review_audit_period_start' | transloco
                }}</span>
                <span class="review-value">{{ form.controls.audit_period_start.value }}</span>
              </div>
            }
            <div class="review-item">
              <span class="review-label">{{
                'modals.create_framework.review_created_by' | transloco
              }}</span>
              <span class="review-value">{{ currentUserName() }}</span>
            </div>
          </div>
        </div>

        @if (selectedTemplateHasPredefinedControls()) {
          <!-- Predefined-controls flow (ISO 27001 today). The control set
               is a hard part of the framework definition — non-selectable.
               We list every control that will be materialised so the user
               sees the exact scope, with a count header for skim-readers
               and a scrollable list for everyone else. -->
          <div class="review-section">
            <div class="predefined-header">
              <p class="section-label">
                {{ 'modals.create_framework.review_predefined_heading' | transloco }}
              </p>
              <!-- Read-only language pill. The pick lives on a dedicated
                   step earlier in the wizard; showing it here confirms
                   what will be materialised without re-offering the
                   choice (which would trigger a stale-preview refetch). -->
              <app-language-badge [language]="templateLanguage()" appearance="pill" />
            </div>
            @if (predefinedTemplatesLoading()) {
              <app-picker-skeleton variant="compact" [count]="4" />
            } @else {
              <!-- One-line coverage summary. The BE ``POST /controls/
                   from-templates`` already dedupes by ``template_id``
                   at write time (existing control → LINKED to the new
                   framework, not duplicated). This line surfaces the
                   accurate "N new · M linked" split *before* commit;
                   the per-row state below tells the user which
                   specific ones. Reuses the ``existing-for-templates``
                   endpoint that drives composite-reuse-panel — one
                   matching primitive, two surfaces. -->
              @if (coverageLoading()) {
                <p class="coverage-summary is-loading">
                  {{ 'modals.create_framework.coverage_loading' | transloco }}
                </p>
              } @else if (coverageError()) {
                <p class="coverage-summary is-warning">
                  {{ 'modals.create_framework.coverage_error' | transloco }}
                </p>
              } @else if (coverageCounts(); as counts) {
                <p class="coverage-summary">
                  @if (counts.linkedCount === 0) {
                    {{
                      'modals.create_framework.coverage_all_new'
                        | transloco: { count: counts.newCount }
                    }}
                  } @else if (counts.newCount === 0) {
                    {{
                      'modals.create_framework.coverage_all_linked'
                        | transloco: { count: counts.linkedCount }
                    }}
                  } @else {
                    {{
                      'modals.create_framework.coverage_mixed'
                        | transloco
                          : {
                              newCount: counts.newCount,
                              linkedCount: counts.linkedCount,
                            }
                    }}
                  }
                </p>
              }

              <!-- One list, per-row state. `.is-linked` rows carry a
                   subtle info tint + a "Will be linked" tag on the
                   right + tooltip disclosing the existing control's
                   name. Default rows read as "will be created". Same
                   visual grammar the composite-reuse-panel uses for
                   `.is-picked` candidate rows — one language across
                   the reuse surfaces. -->
              @if (templateRows().length > 0) {
                <ul class="review-controls-list" role="list">
                  @for (row of templateRows(); track row.template.id) {
                    @let template = row.template;
                    @let linked = row.linkedMatch;
                    @let tIdentity = templateFrameworkIdentity(template);
                    <li
                      class="review-controls-item"
                      [class.is-linked]="linked !== null"
                      [matTooltip]="
                        linked
                          ? ('modals.create_framework.linked_tooltip'
                            | transloco: { name: linked.name })
                          : ''
                      "
                      matTooltipPosition="left"
                    >
                      <span class="review-controls-main">
                        @if (template.control_reference) {
                          <app-control-ref
                            [reference]="template.control_reference"
                            [frameworkType]="tIdentity?.frameworkType ?? null"
                            [frameworkVersion]="tIdentity?.frameworkVersion ?? null"
                            [frameworkName]="tIdentity?.frameworkName ?? null"
                          />
                        }
                        <span class="review-controls-text">
                          <span class="review-controls-name">{{ row.displayName }}</span>
                          @if (row.rationalePreview) {
                            <span class="review-controls-rationale">{{
                              row.rationalePreview
                            }}</span>
                          }
                        </span>
                      </span>
                      @if (linked) {
                        <span class="review-controls-status">
                          {{ 'modals.create_framework.status_will_be_linked' | transloco }}
                        </span>
                      }
                    </li>
                  }
                </ul>
              }
            }
          </div>
        } @else if (pendingImport(); as imp) {
          <div class="review-section">
            <p class="section-label">
              {{ 'modals.create_framework.review_controls_to_import' | transloco }}
            </p>
            <p class="step-hint">
              {{
                'modals.create_framework.review_import_intro'
                  | transloco
                    : {
                        count: imp.control_ids.length,
                        source: selectedSourceFrameworkName() || imp.source_framework_id,
                      }
              }}
            </p>
            @if (pendingImportControls().length > 0) {
              <ul class="review-controls-list">
                @for (control of pendingImportControls(); track control.control_id) {
                  <li class="review-controls-item">
                    <span class="review-controls-name">{{ control.name }}</span>
                    @if (formatControlSubtitle(control); as sub) {
                      <span class="review-controls-sub">{{ sub }}</span>
                    }
                  </li>
                }
              </ul>
            }
          </div>
        } @else if (isManager()) {
          <!-- Non-predefined family, manager skipped Import. Make the
               "empty" state explicit so the user isn't left wondering
               whether something was meant to land here. -->
          <div class="review-section">
            <p class="section-label">
              {{ 'modals.create_framework.review_controls_to_import' | transloco }}
            </p>
            <p class="step-hint">
              {{ 'modals.create_framework.review_no_controls_picked' | transloco }}
            </p>
          </div>
        }
      </div>
    }
  }

  @if (importWarning()) {
    <p class="alert import-warning">{{ importWarning() }}</p>
  }
  @if (importSummary() && successMessage()) {
    <p class="meta import-summary">{{ importSummary() }}</p>
  }
</app-modal>
